<template>
  <div :data-testid="sectionTag" class="w-switch">
    <div
      v-if="currentLabel"
      :data-testid="sectionTag ? `${sectionTag}-label` : undefined"
      class="w-switch__label label-semibold-small"
    >
      {{ currentLabel }}
    </div>
    <button
      :data-testid="sectionTag ? `${sectionTag}-input` : undefined"
      type="button"
      role="switch"
      :aria-checked="modelValue"
      :aria-label="accessibleName"
      :aria-labelledby="accessibleNameId"
      :aria-describedby="describedById"
      :disabled="disabled"
      class="w-switch__track"
      :class="{
        'w-switch__track--selected': modelValue,
        'w-switch__track--disabled': disabled
      }"
      @click="toggle"
    >
      <span class="w-switch__handle" />
    </button>
    <span
      v-if="description"
      :id="descriptionId"
      :data-testid="sectionTag ? `${sectionTag}-description` : undefined"
      class="w-switch__description body-regular-small"
    >
      {{ description }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted } from 'vue'
import { tokens } from '@/plugins/vuetify'

export interface WSwitchProps {
  sectionTag?: string
  /** Visible state text. Never contributes the accessible name — see `ariaLabel`. */
  offLabel?: string
  onLabel?: string
  description?: string
  /**
   * The accessible name, and the only way to name a switch whose purpose is not already in
   * `description` — a bare toggle, or a list row that must identify its row ("Response Tag
   * Notification", not "On"). Falls back to `description`; neither one warns in dev.
   */
  ariaLabel?: string
  /**
   * Id of existing text that describes this switch — a surrounding row's helper text, say. Use it
   * instead of `description` when that text is already on the page, or `description` renders it a
   * second time. Merged with this switch's own description when both are present.
   */
  ariaDescribedby?: string
  modelValue?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<WSwitchProps>(), {
  sectionTag: 'unspecified',
  description: '',
  ariaLabel: undefined,
  ariaDescribedby: undefined,
  modelValue: false,
  disabled: false
})

const emit = defineEmits(['update:modelValue'])

const instance = getCurrentInstance()
const descriptionId = `w-switch-${instance?.uid ?? 'fallback'}-description`

const currentLabel = computed(() => {
  return props.modelValue ? props.onLabel : props.offLabel
})

const hasAriaLabel = computed(() => Boolean(props.ariaLabel?.trim()))
const hasDescription = computed(() => Boolean(props.description?.trim()))

// `ariaLabel` names the switch; `description` only names it when nothing better was given, and
// otherwise stays supplementary. The On/Off div is never the name — it describes state, not purpose,
// and would change every time the switch is toggled.
const accessibleName = computed(() => (hasAriaLabel.value ? props.ariaLabel : undefined))
const accessibleNameId = computed(() => (!hasAriaLabel.value && hasDescription.value ? descriptionId : undefined))
const describedById = computed(() => {
  const own = hasAriaLabel.value && hasDescription.value ? descriptionId : undefined
  return [props.ariaDescribedby?.trim(), own].filter(Boolean).join(' ') || undefined
})

const toggle = (): void => {
  emit('update:modelValue', !props.modelValue)
}

onMounted(() => {
  if (import.meta.env.DEV && !hasAriaLabel.value && !hasDescription.value) {
    console.warn(
      `[WSwitch] switch (sectionTag: "${props.sectionTag}") has no ariaLabel and no description — screen readers will announce it as an unnamed switch. Pass :aria-label to fix.`
    )
  }
})
</script>

<style scoped>
.w-switch {
  display: flex;
  align-items: center;
  gap: v-bind('tokens.spacing.gap.small');
}

.w-switch__label {
  color: v-bind('tokens.color.input.text.description');
}

.w-switch__track {
  position: relative;
  width: 48px;
  height: 26px;
  border-radius: 14px;
  border: none;
  padding: 0;
  cursor: pointer;
  background-color: v-bind('tokens.color.control.background.muted');
  flex-shrink: 0;
  transition: background-color 0.15s ease-in-out;
}

.w-switch__track--selected {
  background-color: v-bind('tokens.color.control.background.enabled');
}

.w-switch__track--disabled {
  background-color: v-bind('tokens.color.control.background.disabled.surface');
  cursor: default;
}

.w-switch__handle {
  position: absolute;
  top: 3px;
  left: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: v-bind('tokens.color.control.background.primary');
  transition: left 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

.w-switch__track--selected .w-switch__handle {
  left: 24px;
}

.w-switch__track--disabled .w-switch__handle {
  background-color: v-bind('tokens.color.control.background.disabled.icon');
}
</style>
